// Welche Seiten es im CRM gibt, welche eine Rolle sieht, und wo sie beginnt
// (E3, Gernots Entscheidung 3, 21.10.2026: „Jede Rolle sieht nur ihre Sache,
// die Leitung sieht alles“). Reine Logik, ohne React – die Tests prüfen sie
// für jede Rolle.
//
// Die Grundregel: Was eine Rolle darf, steht an EINER Stelle – im Kern
// (rechte.ts, `darf`). Hier steht nie eine eigene Liste von Rollen, nur je
// Seite das Recht, das sie braucht; die Seitenleiste zeigt genau die Seiten,
// die die Rolle öffnen darf. Der Server weist ohnehin ab (403
// „keine-berechtigung“) – die Oberfläche soll nur nicht erst anbieten, was
// dann nicht geht.
//
// Die Adressen (hinter dem # – useSeite aus oberflaeche):
//   #/                         die Startseite je Rolle (Leitung, Büro: Übersicht)
//   #/zu-tun                   Zu tun (seit E3c, S2 – Leitung, Büro; an Stelle von „Zu prüfen“)
//   #/pruefen                  alle gesendeten Besichtigungen (die Kiste Sales/Doku;
//                              beim Verkauf: Meine Besichtigungen)
//   #/freigabe/<id>            die Freigabe einer Besichtigung
//   #/freigabe/<id>/material   nur das Material – fürs zweite Fenster
//   #/fragen                   Fragen
//   #/kunden, #/anlage/<id>    Kunden & Anlagen, die Akte einer Anlage
//   #/anlage/<id>/<stelle>     die Akte, gleich an einer Stelle (seit E3c, S2: termin,
//                              bestellung, dachpartie, bodenpartie – die Knöpfe in „Zu tun“)
//   #/material                 Standardmaterial
//   #/partner                  Lieferanten und Partien (seit E3c, die Leitung)
//   #/unterlagen, #/unterlage/<id>   Unterlagen für den Monteur
//   #/kiste/<schlüssel>        eine Kiste, die noch nicht gebaut ist
//   #/leute, #/geraete         Leute und Geräte
//
// Die Materiallisten fürs Lager (#/lager) gibt es seit E3c nicht mehr – ein
// Lager gibt es nicht mehr (H26); an seiner Stelle steht die Kiste
// Bestellung.

import { darf, istKennung, type KistenStufe, type Recht, type Rolle } from "@happy-energy/kern";
import type { IkonName } from "@happy-energy/oberflaeche";
import { kisteFinden, KISTEN_ANZEIGE } from "./kisten.ts";

/** Die Stellen in der Akte, zu denen „Zu tun“ springt (seit E3c, S2) – je eine Karte dort. */
export const AKTE_STELLEN = ["termin", "bestellung", "dachpartie", "bodenpartie"] as const;
export type AkteStelle = (typeof AKTE_STELLEN)[number];

export type Seite =
  | { art: "uebersicht" }
  | { art: "zu-tun" }
  | { art: "pruefen" }
  | { art: "freigabe"; id: string }
  | { art: "material-fenster"; id: string }
  | { art: "fragen" }
  | { art: "kunden" }
  | { art: "anlage"; id: string; stelle?: AkteStelle }
  | { art: "material" }
  | { art: "partner" }
  | { art: "unterlagen" }
  | { art: "unterlage"; id: string }
  | { art: "kiste"; schluessel: string }
  | { art: "leute" }
  | { art: "geraete" }
  | { art: "unbekannt" };

/** Was jede Seite braucht – die Kiste nennt ihr eigenes Recht (kisten.ts). */
const RECHT_JE_SEITE: Record<Exclude<Seite["art"], "kiste" | "unbekannt">, Recht> = {
  uebersicht: "uebersicht",
  "zu-tun": "zu-tun",
  pruefen: "aufnahmen-liste",
  // Lesen dürfen auch Verkauf (die eigenen), Montage und Inbetriebnahme (Freigegebenes) – das Material nur, wer freigibt.
  freigabe: "aufnahme-lesen",
  "material-fenster": "freigeben",
  fragen: "fragen",
  kunden: "kunden",
  anlage: "kunden",
  material: "standardliste",
  partner: "partner-pflegen",
  unterlagen: "unterlagen",
  unterlage: "unterlagen",
  leute: "leute",
  geraete: "leute",
};

/** Das Recht einer Seite – null: Die Seite gibt es nicht. */
export function rechtFuer(seite: Seite): Recht | null {
  if (seite.art === "unbekannt") return null;
  if (seite.art === "kiste") return kisteFinden(seite.schluessel)?.recht ?? "uebersicht";
  return RECHT_JE_SEITE[seite.art];
}

/** Darf diese Rolle die Seite öffnen? Eine unbekannte Seite: nein (sie zeigt „gibt es nicht“). */
export function seiteErlaubt(rolle: Rolle, seite: Seite): boolean {
  const recht = rechtFuer(seite);
  return recht !== null && darf(rolle, recht);
}

/**
 * Wo eine Rolle beginnt – gerechnet aus ihren Rechten, in dieser Reihenfolge:
 * wer den ganzen Weg sieht, die Übersicht; sonst die eigene Kiste (Montage
 * und Inbetriebnahme: die Unterlagen); der Verkauf seine Besichtigungen;
 * zuletzt die Fragen. Die Rolle Lager darf seit E3c nichts mehr – für sie
 * ist auch „/“ „nicht für deine Rolle“.
 */
export function startpfad(rolle: Rolle): string {
  if (darf(rolle, "uebersicht")) return "/";
  if (darf(rolle, "unterlagen")) return "/unterlagen";
  if (darf(rolle, "aufnahmen-liste")) return "/pruefen";
  return "/fragen";
}

/**
 * Sieht diese Rolle nur das, was sie selbst aufgenommen hat? Das ist der
 * Verkauf – so regelt es der Server (crm/waechter.ts, leserFuer); die
 * Oberfläche sagt dann „Meine Besichtigungen“ statt „Besichtigungen“.
 */
export function nurEigene(rolle: Rolle): boolean {
  return rolle === "verkauf";
}

/** Liest die Adresse hinter dem # – „/“ ist die Startseite der Rolle. */
export function seiteLesen(pfad: string, rolle: Rolle): Seite {
  const teile = pfad.replace(/\/+$/, "").split("/").filter(Boolean);
  if (teile.length === 0) {
    const start = startpfad(rolle);
    return start === "/" ? { art: "uebersicht" } : seiteLesen(start, rolle);
  }
  const [a, b, c] = teile;
  const mitKennung = (art: "freigabe" | "anlage" | "unterlage", id: string | undefined): Seite => (istKennung(id) ? { art, id } : { art: "unbekannt" });
  switch (teile.length) {
    case 1:
      switch (a) {
        case "zu-tun":
        case "pruefen":
        case "fragen":
        case "kunden":
        case "material":
        case "partner":
        case "unterlagen":
        case "leute":
        case "geraete":
          return { art: a };
        default:
          return { art: "unbekannt" };
      }
    case 2:
      if (a === "freigabe" || a === "anlage" || a === "unterlage") return mitKennung(a, b);
      if (a === "kiste" && b && kisteFinden(b)) return { art: "kiste", schluessel: b };
      return { art: "unbekannt" };
    case 3:
      if (a === "freigabe" && c === "material" && istKennung(b)) return { art: "material-fenster", id: b };
      if (a === "anlage" && istKennung(b) && (AKTE_STELLEN as readonly string[]).includes(c ?? "")) return { art: "anlage", id: b, stelle: c as AkteStelle };
      return { art: "unbekannt" };
    default:
      return { art: "unbekannt" };
  }
}

// --- Die Seitenleiste ------------------------------------------------------------------------

export interface NavEintrag {
  pfad: string;
  name: string;
  ikon: IkonName;
  /** Die Stufe der Kiste – als Punkt rechts (voll, halb, gestrichelt). */
  punkt?: KistenStufe;
  /** Zeigt eine Zahl: „zu tun“ (seit E3c, S2; vorher „zu prüfen“) – oder die offenen Fragen (seit S6, Gernots Entscheidung 2). */
  zaehler?: "zu-tun" | "fragen";
  /** Weitere Adressen, bei denen der Eintrag hell ist (Leute: auch Geräte). */
  auch?: string[];
}

export interface NavGruppe {
  titel: string;
  eintraege: NavEintrag[];
  /** Unter den Kisten: was die Punkte heißen. */
  legende?: boolean;
}

/**
 * Die Seitenleiste einer Rolle. Wer den ganzen Weg sieht (Leitung, Büro),
 * bekommt drei Gruppen wie im Mockup: „Arbeiten“, „Die Kisten“ (alle acht,
 * mit Punkt für die Stufe) und „Einstellungen“. Wer nur seine Sache sieht
 * (Montage, Inbetriebnahme, Verkauf), bekommt EINE Gruppe – seine
 * Seite zuerst, dann die Fragen; Kisten, die ihn nicht betreffen, stehen
 * nicht da (ausgegraut würden sie fragen lassen, warum man nicht hinein
 * darf – weglassen ist einfacher zu verstehen).
 */
export function navigation(rolle: Rolle): NavGruppe[] {
  const ganzerWeg = darf(rolle, "uebersicht");
  const arbeiten: NavEintrag[] = [];

  // Wer nur seine Kiste sieht: sie zuerst – mit ihrem eigenen Namen („Unterlagen“).
  if (!ganzerWeg) {
    for (const k of KISTEN_ANZEIGE) {
      if (k.recht && k.recht !== "aufnahmen-liste" && darf(rolle, k.recht)) {
        arbeiten.push({ pfad: k.pfad, name: k.eigenerName ?? k.name, ikon: k.ikon });
      }
    }
  }
  if (ganzerWeg) arbeiten.push({ pfad: "/", name: "Übersicht", ikon: "haus" });
  // Seit E3c, S2: „Zu tun“ an Stelle von „Zu prüfen“ – je Anlage, was jetzt geht. Alle gesendeten Besichtigungen
  // stehen weiter unter #/pruefen, erreichbar über die Kiste Sales/Doku.
  if (darf(rolle, "zu-tun")) {
    arbeiten.push({ pfad: "/zu-tun", name: "Zu tun", ikon: "liste", zaehler: "zu-tun" });
  } else if (darf(rolle, "aufnahmen-liste")) {
    arbeiten.push({ pfad: "/pruefen", name: nurEigene(rolle) ? "Meine Besichtigungen" : "Besichtigungen", ikon: "kamera" });
  }
  // Die Zahl der offenen Fragen für jede Rolle – auch für die Montage, die sonst wenig im CRM hat.
  if (darf(rolle, "fragen")) arbeiten.push({ pfad: "/fragen", name: "Fragen", ikon: "frage", zaehler: "fragen" });
  if (darf(rolle, "kunden")) arbeiten.push({ pfad: "/kunden", name: "Kunden & Anlagen", ikon: "gruppe", auch: ["/anlage/"] });

  const gruppen: NavGruppe[] = [{ titel: "Arbeiten", eintraege: arbeiten }];

  if (ganzerWeg) {
    gruppen.push({
      titel: "Die Kisten",
      legende: true,
      eintraege: KISTEN_ANZEIGE.filter((k) => k.recht === null || darf(rolle, k.recht)).map((k) => ({
        pfad: k.pfad,
        name: k.name,
        ikon: k.ikon,
        punkt: k.stufe,
        ...(k.pfad === "/unterlagen" ? { auch: ["/unterlage/"] } : {}),
      })),
    });
  }

  const einstellungen: NavEintrag[] = [];
  if (darf(rolle, "standardliste")) einstellungen.push({ pfad: "/material", name: "Standardmaterial", ikon: "regler" });
  // Seit E3c: bei wem bestellt wird und wer montiert – Vorgaben der Firma, nur die Leitung.
  if (darf(rolle, "partner-pflegen")) einstellungen.push({ pfad: "/partner", name: "Lieferanten und Partien", ikon: "firma" });
  if (darf(rolle, "leute")) einstellungen.push({ pfad: "/leute", name: "Leute und Geräte", ikon: "nutzer", auch: ["/geraete"] });
  if (einstellungen.length > 0) gruppen.push({ titel: "Einstellungen", eintraege: einstellungen });

  return gruppen;
}

/**
 * Welcher Eintrag hell ist: der erste, dessen Adresse passt. Die Freigabe
 * gehört zu „Zu tun“ (seit E3c, S2) – wer das nicht hat, zu seinen
 * Besichtigungen (#/pruefen); das Material-Fenster hat keine Leiste.
 */
export function aktiverPfad(gruppen: readonly NavGruppe[], pfad: string, rolle: Rolle): string | null {
  const wirklich = pfad === "/" || pfad === "" ? startpfad(rolle) : pfad.replace(/\/+$/, "");
  const vergleiche = wirklich.startsWith("/freigabe/") ? ["/zu-tun", "/pruefen"] : [wirklich];
  for (const vergleich of vergleiche) {
    for (const g of gruppen) {
      for (const e of g.eintraege) {
        if (e.pfad === vergleich) return e.pfad;
        if (e.auch?.some((a) => (a.endsWith("/") ? vergleich.startsWith(a) : vergleich === a))) return e.pfad;
      }
    }
  }
  return null;
}
